import moment from 'moment';
import { View } from 'react-native';
import { useState } from 'react';
import { Button } from '@/components/ui/therapist/Button';
import { Icon } from '@/components/Icon';
import { Modal } from '@/components/ui/therapist/Modal';
import { Text } from '@/components/Text';
import { User } from '@/stores/store.types';
import { logError } from '@/utils/logHandling';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { resendAdditionalContactVerificationEmail } from '@/api/services/additionalContact';

import colors from '@/config/colors';
import { styles } from './styles';

interface VerifyEmailProps {
  deleteAdditionalContactInfo: (action: 'delete') => void;
  setEditEmail: React.Dispatch<React.SetStateAction<boolean>>;
  user: User;
}

export const VerifyEmail: React.FC<VerifyEmailProps> = ({
  deleteAdditionalContactInfo,
  setEditEmail,
  user,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const { showNotification } = useNotification();
  const { additionalContact } = user;

  const resendVerificationEmail = async () => {
    try {
      await resendAdditionalContactVerificationEmail();
      showNotification({
        type: 'success',
        title: 'Die Bestätigungs-E-Mail wurde erneut versendet.',
      });
    } catch (error) {
      logError('Error in updateAdditionalContactInfo', error);
      showNotification({
        type: 'error',
        title:
          'Beim erneuten Senden der Bestätigungs-E-Mail ist ein Fehler aufgetreten.',
      });
    }
  };

  const isAdditionalContactVerified = () => {
    if (!additionalContact || !additionalContact.verifiedAt) {
      return false;
    }

    const now = moment();
    return now.isAfter(additionalContact.verifiedAt);
  };

  return (
    <>
      {isAdditionalContactVerified() ? (
        <View style={[styles.fieldWrapper, styles.verifiedFieldWrapper]}>
          <View style={styles.emailWrapper}>
            <View style={{ maxWidth: 350, flexShrink: 1 }}>
              <Text style={styles.verifiedEmail}>
                {additionalContact?.email}
              </Text>
            </View>
            <View style={[styles.pillWrapper, styles.verifiedPillWrapper]}>
              <Icon
                name="checkCircle"
                style={{ color: colors.primary }}
                size={18}
              />
              <Text style={{ color: colors.primary }}>Verifiziert</Text>
            </View>
          </View>
          <View style={styles.buttonWrapper}>
            <Button
              look="ghost"
              style={{ borderColor: colors.primary, borderWidth: 1 }}
              label="Bearbeiten"
              onPress={() => setEditEmail((prevState) => !prevState)}
            />
            <Button
              look="danger"
              style={{ borderColor: colors.red, borderWidth: 1 }}
              label="Entfernen"
              icon="trashAlt"
              onPress={() => setShowDeleteModal(true)}
            />
          </View>
        </View>
      ) : (
        <>
          <View style={[styles.fieldWrapper, styles.unverifiedFieldWrapper]}>
            <View style={styles.emailWrapper}>
              <View style={{ maxWidth: 350, flexShrink: 1 }}>
                <Text style={styles.unverifiedEmail}>
                  {additionalContact?.email}
                </Text>
              </View>
              <View style={[styles.pillWrapper, styles.unverifiedPillWrapper]}>
                <Icon
                  name="clockFour"
                  style={{ color: colors.newUI.therapist.yellow90 }}
                  size={18}
                />
                <Text style={{ color: colors.newUI.therapist.yellow90 }}>
                  Ausstehend
                </Text>
              </View>
            </View>
            <View style={styles.buttonWrapper}>
              <Button
                look="ghost"
                style={{
                  borderColor: colors.primary,
                  borderWidth: 1,
                  backgroundColor: colors.white,
                }}
                label="Erneut senden"
                onPress={resendVerificationEmail}
              />
              <Button
                look="danger"
                style={{ borderColor: colors.red, borderWidth: 1 }}
                label="Entfernen"
                icon="trashAlt"
                onPress={() => setShowDeleteModal(true)}
              />
            </View>
          </View>
          <View style={{ paddingTop: 20 }}>
            <Text
              style={{
                color: colors.newUI.therapist.grey70,
                fontSize: 14,
                marginTop: 8,
              }}
            >
              Eine Bestätigungs-E-Mail wurde an{' '}
              <Text style={{ fontWeight: 600 }}>
                {additionalContact?.email}
              </Text>{' '}
              gesendet. Der Link ist{' '}
              <Text style={{ fontWeight: 600 }}>14 Tage</Text> gültig. Wird die
              E-Mail nicht bestätigt, wird sie automatisch entfernt.
            </Text>
          </View>
        </>
      )}
      <Modal
        isVisible={showDeleteModal}
        title="Zusätzlichen Kontakt entfernen"
        onClose={() => setShowDeleteModal(false)}
        confirmActionButton={
          <Button
            label="Entfernen"
            look="danger"
            onPress={() => deleteAdditionalContactInfo('delete')}
          />
        }
        cancelActionButton={
          <Button
            label="Abbrechen"
            look="secondary"
            onPress={() => setShowDeleteModal(false)}
          />
        }
      >
        <Text>
          Möchten Sie den zusätzlichen Kontakt wirklich entfernen? Nach der
          Entfernung werden keine weiteren E-Mails mehr gesendet. Wenn der
          Kontakt erneut hinzugefügt wird, ist eine neue Verifizierung
          erforderlich.
        </Text>
      </Modal>
    </>
  );
};
